/**
 * useMarkChargeAsPaidManual - Hook para dar baixa manual em uma parcela
 *
 * Fluxo (v2.0.0 — Fonte Única RPC + q_charge):
 * 1. (Opcional) Faz upload do comprovante no bucket 'anexos'
 * 2. Chama a RPC `mark_charge_as_paid_manual` (única porta).
 *    A RPC:
 *      - Persiste a baixa local (parcela + cobrança) atomicamente
 *      - Registra o evento `charge_marked_paid_manual` na timeline
 *      - Quando a parcela está sincronizada (metadata.provider_payment_id)
 *        e o pagamento é total, enfileira `receive_in_cash` em q_charge
 *        para o financial-charge-worker sincronizar com o provider
 *        em segundo plano. Sucesso/erro vira evento na timeline.
 * 3. Invalida caches.
 *
 * Removido em v2.0.0:
 *   - chamada direta à edge function `financial-charge` (action receive_in_cash)
 *   - parâmetro `p_provider_sync_result` da RPC
 *
 * @author Módulo Financeiro
 * RELEASE: 2026-06-09 v1.0.0 - Criação do hook
 * RELEASE: 2026-06-09 v1.1.0 - Sincronização inline com provider via edge function
 * RELEASE: 2026-06-17 v2.0.0 - FONTE ÚNICA: RPC enfileira q_charge internamente.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useMarkChargeAsPaidManual');

export interface MarkAsPaidPayload {
  parcela_id: string;
  cobranca_id: string;
  data_pagamento: string; // YYYY-MM-DD
  valor_pago: number;
  meio_pagamento_id?: string | null;
  descricao?: string | null;
  receipt_file?: File | null;
  /** Valor original da parcela — usado para decidir se é pagamento total (apenas UX) */
  valor_parcela_original?: number;
  /**
   * @deprecated v2.0.0 — A RPC resolve provider_payment_id internamente
   * via metadata da parcela. Mantido apenas para retrocompat de chamadas.
   */
  provider_charge_id?: string | null;
  /** @deprecated v2.0.0 — A RPC resolve conta_corrente_id da cobrança. */
  conta_corrente_id?: string | null;
  /** @deprecated v2.0.0 — sem efeito; default do worker é não notificar. */
  notify_customer?: boolean;
}

export function useMarkChargeAsPaidManual() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: MarkAsPaidPayload) => {
      let receiptPath: string | null = null;

      // 1. Upload do comprovante (opcional) no bucket 'anexos'
      if (payload.receipt_file) {
        const safeName = payload.receipt_file.name.replace(/[^\w.\-]/g, '_');
        const storagePath = `financial/receipts/${payload.cobranca_id}/${payload.parcela_id}-${Date.now()}-${safeName}`;

        const { error: uploadError } = await supabase.storage
          .from('anexos')
          .upload(storagePath, payload.receipt_file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          logger.error('Falha no upload do comprovante', { error: uploadError.message });
          throw new Error(`Falha ao enviar comprovante: ${uploadError.message}`);
        }

        receiptPath = storagePath;
      }

      // 2. RPC única — persiste local e enfileira receive_in_cash quando aplicável
      const { data, error } = await supabase.rpc('mark_charge_as_paid_manual', {
        p_parcela_id: payload.parcela_id,
        p_data_pagamento: payload.data_pagamento,
        p_valor_pago: payload.valor_pago,
        p_meio_pagamento_id: payload.meio_pagamento_id || null,
        p_descricao: payload.descricao || null,
        p_receipt_path: receiptPath,
      });

      if (error) {
        logger.error('Erro ao marcar como paga', { error: error.message });
        throw error;
      }

      return data as {
        success: boolean;
        parcela_status: string;
        cobranca_status: string | null;
        sync_enqueued?: boolean;
      };
    },
    onSuccess: (data, variables) => {
      const enqueued = (data as { sync_enqueued?: boolean })?.sync_enqueued;

      if (enqueued) {
        toast.success('Baixa registrada. Sincronização com o provedor em andamento.');
      } else {
        toast.success('Baixa registrada com sucesso');
      }

      queryClient.invalidateQueries({ queryKey: ['cobranca-details', variables.cobranca_id] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao registrar baixa');
    },
  });
}
